import { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { chatId, description } from '~/lib/persistence/useChatHistory';
import { capture } from '~/lib/posthog';
import { describeRequestError } from '~/lib/request-error';
import { ANALYTICS_EVENTS, INTEGRATIONS, needsBrandingSetup } from '@dunea/shared';
import { formatRelativeTime } from '~/lib/time';
import { apiGetIntegrations } from '~/lib/api/integrations';
import { workbenchStore } from '~/lib/stores/workbench';
// PUBLISH_PAYMENT_REQUIRED_CODE is paused with the $1 unlock — see the note in
// deploy-eligibility.ts. Restore the import when the charge comes back.
import {
  usePlanAccess,
  showPlans,
  blockAndShowPlans,
  capturePaywallBlock,
  handlePlanRequired,
  PLAN_REQUIRED_CODE,
} from '~/lib/planGate';
import {
  apiStaticDeploy,
  apiStaticRedeploy,
  apiChangeStaticSubdomain,
  apiGetStaticDeploymentByChatId,
  apiGetStaticDeployment,
  apiDeleteStaticDeployment,
  apiSetStaticBadge,
  type StaticDeployment,
} from '~/lib/api/deployments';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import { PublishGateModal, type PublishGateReason } from './PublishGateModal';

// Mirrors the server's `assertValidSubdomainLabel`: lowercase letters + numbers
// ONLY (a purely alphanumeric label can never end in a hyphen, the malformation
// that breaks iOS Safari's TLS handshake). 63 is the DNS label max; 3 avoids
// squatting/short-reserved collisions. Reserved-word rejection is enforced
// server-side and surfaced in the error banner.
const MIN_PREFIX_LEN = 3;
const MAX_PREFIX_LEN = 63;

/** Strip anything a subdomain label can't contain, live, as the user types. */
function sanitizePrefix(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, MAX_PREFIX_LEN);
}

/** Returns a user-facing error message, or null when the prefix is acceptable. */
function validatePrefix(prefix: string): string | null {
  if (prefix.length < MIN_PREFIX_LEN) {
    return `The URL must be at least ${MIN_PREFIX_LEN} characters.`;
  }

  return null;
}

// Build-log steps for the deploy progress animation (also shown completed in the live/failed states).
const BUILD_STEPS = [
  'Resolving dependencies',
  'Compiling app routes',
  'Optimising images',
  'Uploading edge bundle',
  'Promoting to production',
] as const;

function projectName(deployment: StaticDeployment | null, desc?: string): string {
  if (deployment?.subdomain) {
    return deployment.subdomain;
  }

  const slug = desc
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'project';
}

function StatusPill({ status, deployedAt }: { status: StaticDeployment['status']; deployedAt?: string | null }) {
  const config = {
    live: { dot: 'ui-dot-ok', label: 'Live', textClass: 'text-success', bgClass: 'bg-success/10 border-success/20' },
    deploying: {
      dot: 'ui-dot-building',
      label: 'Publishing',
      textClass: 'text-info',
      bgClass: 'bg-info/10 border-info/20',
    },
    failed: {
      dot: 'ui-dot-error',
      label: 'Failed',
      textClass: 'text-danger',
      bgClass: 'bg-danger/10 border-danger/20',
    },
    deleted: { dot: 'ui-dot-draft', label: 'Deleted', textClass: 'text-muted', bgClass: 'bg-raised border-hairline' },
  }[status];

  return (
    <div className={classNames('inline-flex items-center gap-2 px-3 py-1.5 rounded-full border', config.bgClass)}>
      <span className={classNames('ui-dot', config.dot, status === 'deploying' && 'animate-pulse')} />
      <span className={classNames('text-xs font-medium', config.textClass)}>{config.label}</span>
      {status === 'live' && deployedAt && (
        <span className="text-[10px] text-faint ml-1">{formatRelativeTime(deployedAt)}</span>
      )}
    </div>
  );
}

function UrlDisplay({
  url,
  label,
  isPrimary,
  onCopy,
  copied,
}: {
  url: string;
  label: string;
  isPrimary?: boolean;
  onCopy: () => void;
  copied: boolean;
}) {
  const displayUrl = url.replace(/^https?:\/\//, '');

  return (
    <div
      className={classNames(
        'group relative rounded-xl border transition-all duration-200',
        isPrimary
          ? 'bg-gradient-to-br from-success/[0.06] to-transparent border-success/20'
          : 'bg-raised border-hairline hover:border-subtle',
      )}
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <div className={classNames('i-ph:globe-simple text-sm', isPrimary ? 'text-success' : 'text-faint')} />
          <span className="ui-caption">{label}</span>
          {isPrimary && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-success/15 text-success">
              Primary
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <code
            className={classNames(
              'flex-1 text-sm font-geist-mono truncate',
              isPrimary ? 'text-success' : 'text-secondary',
            )}
          >
            {displayUrl}
          </code>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
            <button
              onClick={onCopy}
              aria-label="Copy URL"
              className={classNames(
                'p-1.5 touch:p-2.5 rounded-md transition-all',
                copied ? 'bg-success/20 text-success' : 'bg-raised text-muted hover:bg-sunken hover:text-secondary',
              )}
              title="Copy URL"
            >
              <div className={copied ? 'i-ph:check-bold text-xs' : 'i-ph:copy text-xs'} />
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 touch:p-2.5 rounded-md bg-raised text-muted hover:bg-sunken hover:text-secondary transition-all"
              title="Open in new tab"
              aria-label="Open in new tab"
            >
              <div className="i-ph:arrow-square-out text-xs" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DeploymentPanelProps {
  onClose?: () => void;
  onDeploymentChange?: (d: StaticDeployment | null) => void;
}

export function DeploymentPanel({ onClose, onDeploymentChange }: DeploymentPanelProps = {}) {
  const currentChatId = useStore(chatId);
  const desc = useStore(description);

  const [deployment, setDeployment] = useState<StaticDeployment | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Integrations that are live on Dunea's shared credentials and could be branded.
  const [brandingHints, setBrandingHints] = useState<string[]>([]);

  // Subdomain-prefix editing. `pendingSubdomain` holds a prefix chosen BEFORE the
  // first deploy (applied when Deploy runs); a live app applies the change
  // immediately via `apiChangeStaticSubdomain`.
  const [editingPrefix, setEditingPrefix] = useState(false);
  const [prefixDraft, setPrefixDraft] = useState('');
  const [prefixError, setPrefixError] = useState<string | null>(null);
  const [pendingSubdomain, setPendingSubdomain] = useState<string | null>(null);
  const [savingPrefix, setSavingPrefix] = useState(false);

  // "Made with Dunea" badge toggle. Free plans always ship the badge (the
  // switch routes them to the plans dialog); paid plans may turn it off.
  // `pendingShowBadge` holds a choice made BEFORE the first deploy.
  const { tier, isFree, planUnknown } = usePlanAccess();
  const [pendingShowBadge, setPendingShowBadge] = useState<boolean | null>(null);
  const [savingBadge, setSavingBadge] = useState(false);

  // Publishing is paid — clicking Publish without entitlement opens the paywall
  // popup (server enforces the same gate, so this is UX not security). `reason`
  // picks the variant: 'subscribe' for free, 'pay' for Starter's $1-per-chat.
  const [gateOpen, setGateOpen] = useState(false);
  const [gateReason, setGateReason] = useState<PublishGateReason>('subscribe');

  // Drives the animated build-log progress (0..BUILD_STEPS.length).
  const [buildStep, setBuildStep] = useState(0);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const panelBlockCounted = useRef(false);

  /*
   * A free user reached the Publish panel and met the locked banner. Counted
   * once per mount, and only once the plan has actually resolved — `planUnknown`
   * would otherwise book a paying user as blocked while billing loads.
   *
   * This is the denominator for the `deploy` / `redeploy` entries: without it,
   * "people don't want to publish" and "people never found publishing" produce
   * the same silence, and they call for opposite fixes.
   */
  useEffect(() => {
    if (!isFree || planUnknown || panelBlockCounted.current) {
      return;
    }

    panelBlockCounted.current = true;
    capturePaywallBlock('deploy_panel', { tier, chatId: currentChatId });
  }, [isFree, planUnknown, tier, currentChatId]);

  const updateDeployment = useCallback(
    (d: StaticDeployment | null) => {
      setDeployment(d);
      onDeploymentChange?.(d);
    },
    [onDeploymentChange],
  );

  // Cleanup intervals on unmount.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }

      if (stepRef.current) {
        clearInterval(stepRef.current);
      }
    };
  }, []);

  // When live, flag any integration still using Dunea's shared credentials.
  useEffect(() => {
    if (deployment?.status !== 'live' || !deployment.id) {
      setBrandingHints([]);
      return undefined;
    }

    let cancelled = false;
    apiGetIntegrations(deployment.id)
      .then((states) => {
        if (cancelled) {
          return;
        }

        const hints = states
          .filter((s) => s.enabled)
          .map((s) => ({ manifest: INTEGRATIONS.find((i) => i.id === s.integrationId), mode: s.credentialMode }))
          .filter((x) => x.manifest && needsBrandingSetup(x.manifest, x.mode))
          .map((x) => x.manifest!.title);
        setBrandingHints(hints);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [deployment?.status, deployment?.id]);

  // Advance the animated build log while deploying.
  useEffect(() => {
    if (!deploying) {
      if (stepRef.current) {
        clearInterval(stepRef.current);
        stepRef.current = null;
      }

      return undefined;
    }

    stepRef.current = setInterval(() => {
      setBuildStep((s) => (s >= BUILD_STEPS.length - 1 ? s : s + 1));
    }, 900);

    return () => {
      if (stepRef.current) {
        clearInterval(stepRef.current);
        stepRef.current = null;
      }
    };
  }, [deploying]);

  const startPolling = useCallback(
    (deploymentId: string) => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }

      pollRef.current = setInterval(async () => {
        try {
          const updated = await apiGetStaticDeployment(deploymentId);

          setDeployment((prev) => {
            if (prev && prev.status === updated.status && prev.deployStep === updated.deployStep) {
              return prev;
            }

            onDeploymentChange?.(updated);

            return updated;
          });

          if (updated.status === 'live' || updated.status === 'failed') {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setDeploying(false);
            setBuildStep(BUILD_STEPS.length);

            if (updated.status === 'live') {
              capture(ANALYTICS_EVENTS.DEPLOYMENT_SUCCEEDED, { deploymentId, deployUrl: updated.deployUrl });
            } else if (updated.status === 'failed') {
              setError(updated.errorMessage || 'Deployment failed');
              capture(ANALYTICS_EVENTS.DEPLOYMENT_FAILED, {
                deploymentId,
                error: updated.errorMessage || 'Unknown error',
              });
            }
          }
        } catch {
          // Ignore polling errors
        }
      }, 2000);
    },
    [onDeploymentChange],
  );

  useEffect(() => {
    if (!currentChatId) {
      return;
    }

    apiGetStaticDeploymentByChatId(currentChatId).then((dep) => {
      updateDeployment(dep);

      if (dep?.status === 'deploying') {
        setDeploying(true);
        setBuildStep(0);
        startPolling(dep.id);
      } else if (dep?.status === 'live' || dep?.status === 'failed') {
        setBuildStep(BUILD_STEPS.length);
      }
    });
  }, [currentChatId, startPolling, updateDeployment]);

  const handleRefresh = useCallback(async () => {
    if (!deployment) {
      return;
    }

    setRefreshing(true);

    try {
      const updated = await apiGetStaticDeployment(deployment.id);
      updateDeployment(updated);
    } catch {
      // Ignore
    } finally {
      setRefreshing(false);
    }
  }, [deployment, updateDeployment]);

  const handleDeploy = useCallback(async () => {
    if (!currentChatId) {
      return;
    }

    // Publishing is a paid-plan action — a free user gets the paywall up front
    // instead of a deploy the server would refuse. Every paid plan, Starter
    // included, deploys for free. (planUnknown → let it through to the server
    // gate rather than blocking a paying user on a slow billing query.)
    if (isFree) {
      // The block moment. The server never sees this click, so if we don't count
      // it here nothing does — see capturePaywallBlock.
      capturePaywallBlock(deployment ? 'redeploy' : 'deploy', { tier, chatId: currentChatId });
      setGateReason('subscribe');
      setGateOpen(true);

      return;
    }

    setDeploying(true);
    setError(null);
    setBuildStep(0);

    const isRedeploy = !!deployment;
    capture(ANALYTICS_EVENTS.DEPLOYMENT_STARTED, { chatId: currentChatId, isRedeploy });

    try {
      // First deploy carries any pre-deploy subdomain/badge choices; a redeploy
      // reuses the app's existing settings.
      const result = deployment
        ? await apiStaticRedeploy(deployment.id)
        : await apiStaticDeploy(currentChatId, pendingSubdomain ?? undefined, pendingShowBadge ?? undefined);
      setPendingSubdomain(null);
      setPendingShowBadge(null);
      updateDeployment(result);
      startPolling(result.id);

      // Plugin pre-publish warnings (e.g. checkout still in test mode): the
      // publish goes ahead, but the owner should know what won't work live.
      for (const warning of result.checkWarnings ?? []) {
        toast.warning(warning, { autoClose: 10_000 });
      }
    } catch (err: any) {
      /*
       * Blocked by the deploy gate — show the paywall, not an error.
       *
       * PAUSED 2026-09-04: the second branch, PUBLISH_PAYMENT_REQUIRED → Starter
       * needs the $1 unlock for this chat ('pay'). Every paid plan deploys free
       * now, so only PLAN_REQUIRED can come back. Kept commented so the charge
       * can be restored — see deploy-eligibility.ts.
       *
       *   if (code === PLAN_REQUIRED_CODE || code === PUBLISH_PAYMENT_REQUIRED_CODE) {
       *     setGateReason(code === PUBLISH_PAYMENT_REQUIRED_CODE ? 'pay' : 'subscribe');
       *   }
       */
      const code = err?.response?.data?.code;

      if (code === PLAN_REQUIRED_CODE) {
        setDeploying(false);
        setBuildStep(0);
        setGateReason('subscribe');
        setGateOpen(true);

        return;
      }

      console.error(err);

      // Record what the server said, not axios's "Request failed with status
      // code 400" — that alone made every deploy failure undiagnosable.
      const details = describeRequestError(err);
      setError(details.serverMessage || 'Deployment failed. Please try again.');
      capture(ANALYTICS_EVENTS.DEPLOYMENT_FAILED, {
        chatId: currentChatId,
        isRedeploy,
        error: details.serverMessage || details.error,
        serverMessage: details.serverMessage,
        code: details.code,
        status: details.status,
        clientError: details.error,
      });
      setDeploying(false);
      setBuildStep(0);
    }
  }, [currentChatId, deployment, isFree, tier, pendingSubdomain, pendingShowBadge, startPolling, updateDeployment]);

  /*
   * PAUSED 2026-09-04: resuming a deploy after the $1 publish-unlock checkout
   * (`?publish=unlocked&chatId=…`). Nothing sends the user to that checkout any
   * more — every paid plan deploys free — so there is no return to handle.
   * Kept commented so the charge can be restored; see deploy-eligibility.ts.
   *
   *   // Returning from the $1 publish-unlock checkout (`?publish=unlocked&chatId=…`):
   *   // the webhook has (usually) granted the unlock, so auto-resume the deploy and
   *   // strip the params. If the webhook is still in flight the server re-refuses and
   *   // the "$1 to deploy" popup simply reopens — the user retries. Runs once.
   *   const resumedUnlockRef = useRef(false);
   *   useEffect(() => {
   *     if (resumedUnlockRef.current || !currentChatId) {
   *       return;
   *     }
   *
   *     const params = new URLSearchParams(window.location.search);
   *
   *     if (params.get('publish') !== 'unlocked' || params.get('chatId') !== currentChatId) {
   *       return;
   *     }
   *
   *     resumedUnlockRef.current = true;
   *     params.delete('publish');
   *     params.delete('chatId');
   *
   *     const query = params.toString();
   *     window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
   *
   *     handleDeploy();
   *   }, [currentChatId, handleDeploy]);
   */

  const handleDelete = useCallback(async () => {
    if (!deployment) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await apiDeleteStaticDeployment(deployment.id);
      updateDeployment(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [deployment, updateDeployment]);

  const handleCopy = () => {
    if (deployment?.deployUrl) {
      const url = deployment.customDomain ? `https://${deployment.customDomain}` : deployment.deployUrl;
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Effective badge state shown by the switch: an unsaved pre-deploy choice
  // wins, then the server's flag, defaulting to on.
  const showBadge = pendingShowBadge ?? deployment?.showBadge ?? true;

  const toggleBadge = useCallback(async () => {
    if (planUnknown || savingBadge || deploying) {
      return;
    }

    const next = !showBadge;

    // Hiding the badge is paid-only — send free users to the plans dialog
    // instead of a switch that will 403. (Server enforces regardless.)
    if (!next && isFree) {
      blockAndShowPlans('remove_badge', { tier, chatId: currentChatId });
      return;
    }

    if (!deployment) {
      setPendingShowBadge(next);
      return;
    }

    setSavingBadge(true);

    try {
      const updated = await apiSetStaticBadge(deployment.id, next);
      updateDeployment(updated);
      setPendingShowBadge(null);
    } catch (err: any) {
      if (!handlePlanRequired(err)) {
        setError(err?.response?.data?.message || 'Could not update the badge setting. Please try again.');
      }
    } finally {
      setSavingBadge(false);
    }
  }, [planUnknown, savingBadge, deploying, showBadge, isFree, tier, currentChatId, deployment, updateDeployment]);

  // Enter edit mode with the current prefix pre-sanitised so it's valid to start.
  const startEditPrefix = useCallback((prefix: string) => {
    setPrefixDraft(sanitizePrefix(prefix));
    setPrefixError(null);
    setEditingPrefix(true);
  }, []);

  const cancelEditPrefix = useCallback(() => {
    setEditingPrefix(false);
    setPrefixError(null);
  }, []);

  const savePrefix = useCallback(async () => {
    const clean = sanitizePrefix(prefixDraft);
    const invalid = validatePrefix(clean);

    if (invalid) {
      setPrefixError(invalid);
      return;
    }

    // No change against the live subdomain → just close the editor.
    if (deployment && clean === deployment.subdomain) {
      setEditingPrefix(false);
      setPendingSubdomain(null);

      return;
    }

    if (!deployment) {
      // First deploy: stash the choice; handleDeploy sends it to the server.
      setPendingSubdomain(clean);
      setEditingPrefix(false);

      return;
    }

    // Live app: apply the rename now. The server redeploys under the new
    // subdomain (reusing the app's D1) and retires the old worker.
    setSavingPrefix(true);
    setPrefixError(null);
    setError(null);

    try {
      const updated = await apiChangeStaticSubdomain(deployment.id, clean);
      updateDeployment(updated);
      setPendingSubdomain(null);
      setEditingPrefix(false);
      setBuildStep(BUILD_STEPS.length);
    } catch (err: any) {
      setPrefixError(err?.response?.data?.message || 'Could not change the URL. Please try again.');
    } finally {
      setSavingPrefix(false);
    }
  }, [prefixDraft, deployment, updateDeployment]);

  const isLive = deployment?.status === 'live';
  const isFailed = deployment?.status === 'failed';
  const canDeploy = !deploying && !deleting;
  // The subdomain prefix (the editable label before `.dunea.app`). A prefix
  // chosen before the first deploy (pendingSubdomain) previews here immediately.
  const prefix = pendingSubdomain ?? projectName(deployment, desc);
  const name = prefix;
  const elapsed = String(buildStep * 14).padStart(2, '0');

  if (!currentChatId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-surface">
        <div className="w-16 h-16 rounded-2xl bg-raised border border-hairline flex items-center justify-center mb-4">
          <div className="i-ph:cloud-slash text-2xl text-faint" />
        </div>
        <p className="text-sm text-muted text-center">No project selected</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden bg-surface">
      <div className="h-full overflow-y-auto">
        <div className="max-w-xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-primary mb-1 font-geist">{isLive ? 'Published' : 'Publish'}</h2>
              <p className="text-sm text-muted">
                {isLive ? 'Your landing page is live' : 'Publish your landing page to the web'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {deployment && (
                <>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className={classNames('ui-btn-icon', refreshing && 'opacity-50')}
                    title="Refresh status"
                  >
                    <div className={classNames('i-ph:arrows-clockwise text-sm', refreshing && 'animate-spin')} />
                  </button>
                  <StatusPill status={deployment.status} deployedAt={deployment.deployedAt} />
                </>
              )}

              {onClose && (
                <button onClick={onClose} className="ui-btn-icon" title="Close">
                  <div className="i-ph:x text-sm" />
                </button>
              )}
            </div>
          </div>

          {/*
           * Free plan: say so before they click, not after. Publishing is
           * paid-plan only (every paid plan, Starter included, publishes free),
           * and a banner up front beats a button that opens a paywall. Only a
           * LOADED free plan shows it — `planUnknown` keeps a paying user from
           * flashing a locked state while billing resolves.
           */}
          {isFree && !planUnknown && (
            <div className="mb-6 rounded-2xl border border-warning/25 bg-warning/[0.06] p-4">
              <div className="flex items-start gap-3">
                <div className="i-ph:lock-simple-fill mt-0.5 flex-shrink-0 text-[15px] text-warning" />
                <div className="min-w-0">
                  <div className="font-geist text-sm font-medium text-primary">
                    You need an active paid plan to publish
                  </div>
                  <p className="mt-1 font-geist text-[12px] leading-relaxed text-secondary">
                    Publishing takes your site live on the web. It&rsquo;s included on every paid plan &mdash; unlimited
                    publishes and republishes, on Plus and Pro.
                  </p>
                  <button
                    type="button"
                    onClick={() => showPlans('deploy_panel')}
                    className="ui-btn-accent mt-3 px-4 py-1.5 text-[12px]"
                  >
                    <span>See plans</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* No Deployment State */}
          {!deployment && !deploying && (
            <div className="mb-6">
              <div className="rounded-2xl bg-raised border border-hairline p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <div className="i-ph:rocket-launch text-3xl text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2 font-geist">Ready to publish</h3>
                <p className="text-sm text-muted max-w-sm mx-auto">
                  Your landing page will be published to a fast global CDN with instant SSL.
                </p>
              </div>
            </div>
          )}

          {/* Deploying State */}
          {deploying && (
            <div className="mb-6 rounded-2xl bg-accent/[0.06] border border-accent/20 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
                  <div className="i-svg-spinners:90-ring-with-bg text-lg text-accent" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-primary">Publishing your page</h3>
                  <p className="text-xs text-muted">This usually takes less than a minute</p>
                </div>
              </div>
            </div>
          )}

          {/* Failed State */}
          {isFailed && (
            <div className="mb-6 rounded-2xl bg-danger/[0.08] border border-danger/20 p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger/15 flex items-center justify-center flex-shrink-0">
                  <div className="i-ph:x-circle text-lg text-danger" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-danger mb-1">Publish failed</h3>
                  {deployment?.errorMessage && (
                    <p className="text-xs text-danger/70 break-words">{deployment.errorMessage}</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleDeploy}
                disabled={!canDeploy}
                className="mt-4 w-full py-2.5 rounded-xl text-sm font-medium bg-danger/10 border border-danger/20 text-danger hover:bg-danger/15 transition-colors"
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="i-ph:arrow-counter-clockwise text-sm" />
                  Retry
                </div>
              </button>
            </div>
          )}

          {/* Error Banner */}
          {error && !isFailed && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/20">
              <div className="flex items-start gap-2">
                <div className="i-ph:warning-circle text-sm text-danger mt-0.5" />
                <p className="text-xs text-danger flex-1">{error}</p>
                <button onClick={() => setError(null)} className="text-danger/50 hover:text-danger">
                  <div className="i-ph:x text-sm" />
                </button>
              </div>
            </div>
          )}

          {/* Live State */}
          {isLive && (
            <div className="space-y-4 mb-6">
              <UrlDisplay
                url={deployment?.customDomain ? `https://${deployment.customDomain}` : deployment?.deployUrl || ''}
                label={deployment?.customDomain ? 'Custom Domain' : 'Live URL'}
                isPrimary
                onCopy={handleCopy}
                copied={copied}
              />

              {/* Branding hint — integrations live on Dunea's shared credentials */}
              {brandingHints.length > 0 && (
                <div className="px-4 py-3 rounded-xl bg-warning/10 border border-warning/20">
                  <div className="flex items-start gap-2">
                    <div className="i-ph:info text-sm text-warning mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-secondary">
                        {brandingHints.join(' and ')} {brandingHints.length > 1 ? 'are' : 'is'} live using Dunea’s
                        shared sign-in — users see “Dunea” on the consent screen. Add your own credentials to show your
                        brand.
                      </p>
                      <button
                        onClick={() => workbenchStore.openTool('integrations')}
                        className="mt-2 text-xs font-medium text-warning hover:underline"
                      >
                        Set up branding →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={handleDeploy}
                  disabled={!canDeploy}
                  className={classNames(
                    'flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all',
                    canDeploy
                      ? 'bg-gradient-to-b from-[#D9F26A] to-[rgba(190,225,90,1)] text-[#0A0A0A] shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98]'
                      : 'bg-raised text-faint cursor-not-allowed',
                  )}
                >
                  {deploying ? (
                    <>
                      <div className="i-svg-spinners:90-ring-with-bg" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <div className="i-ph:arrows-clockwise" />
                      Republish
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={!canDeploy}
                  className={classNames(
                    'flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all',
                    'bg-raised border border-hairline',
                    canDeploy
                      ? 'text-danger hover:bg-danger/10 hover:border-danger/30'
                      : 'text-faint cursor-not-allowed',
                  )}
                >
                  {deleting ? (
                    <>
                      <div className="i-svg-spinners:90-ring-with-bg" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <div className="i-ph:trash" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Domain — editable subdomain prefix. Hidden while a deploy is in flight. */}
          {!deploying && (
            <div className="mb-6 rounded-2xl bg-raised border border-hairline p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="i-ph:globe-simple text-sm text-faint" />
                <span className="ui-caption">Domain</span>
              </div>

              <div className="flex items-center gap-2 px-3 py-2.5 bg-page border border-hairline rounded-xl text-sm">
                {editingPrefix ? (
                  <>
                    {/* Editable prefix; `.dunea.app` stays locked beside it. */}
                    <input
                      autoFocus
                      value={prefixDraft}
                      disabled={savingPrefix}
                      onChange={(e) => {
                        const clean = sanitizePrefix(e.target.value);
                        setPrefixDraft(clean);
                        setPrefixError(validatePrefix(clean));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          savePrefix();
                        } else if (e.key === 'Escape') {
                          cancelEditPrefix();
                        }
                      }}
                      placeholder="mysite"
                      className="flex-1 min-w-0 bg-transparent font-geist-mono text-primary outline-none placeholder:text-faint"
                    />
                    <span className="font-geist-mono text-muted flex-shrink-0">.dunea.app</span>
                    <button
                      className="ui-btn-icon text-success"
                      onClick={savePrefix}
                      disabled={savingPrefix || !!prefixError}
                      title="Save"
                    >
                      {savingPrefix ? (
                        <span className="i-svg-spinners:90-ring-with-bg text-[13px]" />
                      ) : (
                        <div className="i-ph:check-bold text-[13px]" />
                      )}
                    </button>
                    <button className="ui-btn-icon" onClick={cancelEditPrefix} disabled={savingPrefix} title="Cancel">
                      <div className="i-ph:x text-[13px]" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-geist-mono truncate min-w-0">
                      <span className="text-primary">{prefix}</span>
                      {/* Locked platform suffix — never editable. */}
                      <span className="text-muted">.dunea.app</span>
                    </span>
                    <div className="flex-1" />
                    <button
                      className="ui-btn-icon"
                      onClick={() =>
                        // A rename republishes the site, so on free it opens the plans like Publish does.
                        isFree && !planUnknown && deployment
                          ? blockAndShowPlans('rename', { tier, chatId: currentChatId })
                          : startEditPrefix(prefix)
                      }
                      title="Edit URL"
                    >
                      <div className="i-ph:pencil-simple text-[13px]" />
                    </button>
                  </>
                )}
              </div>

              {editingPrefix && prefixError && <p className="mt-2 text-[11px] text-danger font-geist">{prefixError}</p>}
              {editingPrefix && !prefixError && (
                <p className="mt-2 text-[11px] text-muted font-geist">
                  Lowercase letters and numbers only.{' '}
                  {deployment ? 'Saving redeploys your app at the new URL.' : 'Applied when you deploy.'}
                </p>
              )}
            </div>
          )}

          {/* "Made with Dunea" badge. Hidden while a deploy is in flight. */}
          {!deploying && (
            <div className="mb-6 rounded-2xl bg-raised border border-hairline p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="i-ph:seal-check text-sm text-faint" />
                <span className="ui-caption">Site badge</span>
              </div>

              <div className="flex items-center gap-3 px-3 py-2.5 bg-page border border-hairline rounded-xl">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-primary font-geist">Show “Made with Dunea” badge</div>
                  <div className="mt-0.5 text-[11px] text-muted font-geist">
                    {isFree && !planUnknown ? (
                      <>
                        Included on the free plan.{' '}
                        <button
                          type="button"
                          className="text-accent hover:underline"
                          onClick={() => showPlans('badge_note')}
                        >
                          Upgrade to remove
                        </button>
                      </>
                    ) : deployment && showBadge !== deployment.showBadge ? (
                      'Applied when you deploy.'
                    ) : isLive ? (
                      'Changes apply on your next deploy.'
                    ) : (
                      'Shown in the corner of your published site.'
                    )}
                  </div>
                </div>
                {savingBadge ? (
                  <span className="i-svg-spinners:90-ring-with-bg text-[14px] text-muted flex-shrink-0" />
                ) : (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showBadge}
                    aria-label="Show Made with Dunea badge"
                    onClick={toggleBadge}
                    disabled={planUnknown}
                    className={classNames(
                      'ui-switch flex-shrink-0',
                      showBadge && 'ui-switch-on',
                      planUnknown && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <span className={classNames('ui-switch-thumb', showBadge && 'ui-switch-thumb-on')} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Build log — shown while deploying and completed in the live/failed states. */}
          {(deploying || deployment) && (
            <div className="mb-6 rounded-2xl bg-raised border border-hairline p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="ui-caption">Build log</span>
                {deploying && <span className="ui-dot ui-dot-live ui-pulsing" />}
                {isLive && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-success">
                    <div className="i-ph:check-bold text-[10px]" />
                    shipped
                  </span>
                )}
                {isFailed && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-danger">
                    <div className="i-ph:x-circle text-[11px]" />
                    failed
                  </span>
                )}
                <div className="flex-1" />
                <span className="font-geist-mono text-[11px] text-muted">00:{elapsed}</span>
              </div>

              <div className="border border-hairline rounded-xl overflow-hidden bg-page">
                {BUILD_STEPS.map((label, i) => {
                  const done = i < buildStep;
                  const current = i === buildStep && deploying;

                  return (
                    <div
                      key={label}
                      className={classNames(
                        'flex items-center gap-2.5 px-3.5 py-2.25 text-[12px]',
                        i ? 'border-t border-hairline' : '',
                      )}
                    >
                      <span
                        className={classNames(
                          'w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0',
                          done
                            ? 'bg-success border-success text-[#0A0A0A]'
                            : current
                              ? 'bg-accent border-accent'
                              : 'bg-transparent border-prominent',
                        )}
                      >
                        {done && <div className="i-ph:check-bold text-[8px]" />}
                        {current && <span className="ui-dot ui-pulsing bg-[#0A0A0A] w-[5px] h-[5px]" />}
                      </span>
                      <span className={classNames('flex-1', i <= buildStep ? 'text-primary' : 'text-muted')}>
                        {label}
                      </span>
                      <span className="font-geist-mono text-[10.5px] text-faint">
                        {done ? `${8 + i * 2}s` : current ? '…' : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Deploy history */}
          <div className="mb-6 rounded-2xl bg-raised border border-hairline p-4">
            <div className="ui-caption mb-3">Deploy history</div>
            <div className="border border-hairline rounded-xl overflow-hidden bg-page">
              {deployment ? (
                <div className="flex items-center gap-2.5 px-3.5 py-2.25 text-[12.5px]">
                  <span className="font-geist-mono text-muted w-8">v1</span>
                  <span className="flex-1 text-primary truncate">{name}</span>
                  <span className="text-[11px] text-muted">
                    {deployment.deployedAt ? formatRelativeTime(deployment.deployedAt) : '—'}
                  </span>
                  <span className={classNames('ui-pill', isLive ? 'text-accent border-accent/40' : 'text-muted')}>
                    {isLive ? 'live' : isFailed ? 'failed' : 'building'}
                  </span>
                </div>
              ) : (
                <div className="px-3.5 py-3 text-[12px] text-muted font-geist text-center">No deployments yet</div>
              )}
            </div>
          </div>

          {/* Publish Button (when not live) */}
          {!isLive && !isFailed && !deploying && (
            <button
              onClick={handleDeploy}
              disabled={!canDeploy}
              className={classNames(
                'w-full justify-center py-4 text-sm font-semibold',
                canDeploy
                  ? 'ui-btn-accent hover:scale-[1.01] active:scale-[0.99]'
                  : 'flex items-center gap-2 rounded-xl bg-raised text-faint cursor-not-allowed',
              )}
            >
              <div className="i-ph:rocket-launch" />
              Publish Now
            </button>
          )}
        </div>
      </div>

      <PublishGateModal open={gateOpen} reason={gateReason} chatId={currentChatId} onClose={() => setGateOpen(false)} />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDelete();
        }}
        title="Delete this deployment?"
        description="This can't be undone."
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleting}
      />
    </div>
  );
}
